import { createStore } from 'vuex';
import { createLogger } from 'vuex/dist/vuex.esm-browser.prod';
import { cloneDeep } from 'lodash';
import {
  layout,
  user,
  memorial,
  church,
  funeral,
  emoticon,
  common,
  welldying,
  item,
  family,
  law,
  faq,
  inquiry,
  notice,
  payment,
  premium,
  notification,
  announcement,
  diary,
  business,
  smile,
  invitation,
  cry,
  cry_gallery,
  andersen,
  card,
  flyer
} from './modules';

import createPersistedState from 'vuex-persistedstate';
import ConstCode from '@/common/constants/ConstCode';

const embeddedLogger = createLogger({
  filter(mutation, stateBefore, stateAfter) {
    console.debug('BEFORE :', stateBefore[mutation.type.substr(0, mutation.type.lastIndexOf('/'))]['position']);
    console.debug('AFTER :', stateAfter[mutation.type.substr(0, mutation.type.lastIndexOf('/'))]['position']);
  },
  logger: console
});

const { CODE_ENV_LIST } = ConstCode;
const { PROJECT_NAME } = CODE_ENV_LIST;

const MODULES = {
  layout,
  user,
  memorial,
  church,
  funeral,
  emoticon,
  common,
  welldying,
  item,
  family,
  law,
  faq,
  inquiry,
  notice,
  payment,
  premium,
  notification,
  announcement,
  diary,
  business,
  smile,
  invitation,
  cry,
  cry_gallery,
  andersen,
  card,
  flyer
};

// 260515: 모듈 정의 시점의 초기 state를 미리 deep-clone. createStore + persistedstate 하이드레이션이
// store.state 안의 객체를 reactive proxy로 감싸기 전에 캡처해야 한다.
const PRISTINE_STATE = cloneDeep(
  Object.fromEntries(Object.entries(MODULES).map(([key, mod]) => [key, mod.state]))
);

// 260515: 로그아웃 시 보존할 모듈. 사용자 무관 정적 캐시·UI 플래그만 유지하고 나머지는 전부 초기화.
// emoticon은 정적 캐시처럼 보이나 buyList/buy.userNo 등 user-scoped state 보유 → 보존 대상 아님.
const PRESERVE_ON_LOGOUT = ['layout', 'common', 'faq', 'law', 'notice'];

const store = createStore({
  modules: MODULES,

  plugins: [
    createPersistedState({
      // Storage에 저장할 모듈 선택
      key: PROJECT_NAME,
      paths: ['user', 'business.recentSearch', 'business.addressList', 'business.jobRegister', 'welldying.detail', 'layout.isTodayExpireDate', 'layout.isIntroEnter']
    })
    // embeddedLogger
  ]
});

// 260515: 로그아웃 시 모든 도메인 모듈 state를 초기값으로 리셋.
// 이전엔 user 모듈만 비워서 business.businessList 등 다른 모듈에 A 계정 데이터가 잔존 →
// B 계정 로그인 후 간판설정에 A 간판이 그대로 보이는 누수가 발생했음.
// replaceState는 vuex-persistedstate subscriber를 트리거하지 않아 localStorage가 안 갱신됨 →
// cross-refresh 시 A의 persisted 경로(business.recentSearch 등)가 재하이드레이션되는 누수가 있음.
// PROJECT_NAME 키 자체를 제거해 다음 부팅에 모듈 정의값으로 시작하도록 한다.
// trade-off: layout.isIntroEnter/isTodayExpireDate 플래그도 함께 초기화 → 다음 부팅 시 1회 재노출.
export const resetStoreOnLogout = () => {
  const next = cloneDeep(PRISTINE_STATE);
  PRESERVE_ON_LOGOUT.forEach(key => {
    if (store.state[key]) next[key] = cloneDeep(store.state[key]);
  });
  store.replaceState(next);
  try {
    localStorage.removeItem(PROJECT_NAME);
    // 260515 P1-2: 비즈니스 마법사 done 마킹도 함께 제거. persistedstate가 아닌 직접 write라
    // PROJECT_NAME 키 삭제만으론 다른 계정 로그인 후 wizard 우회 잔존 가능.
    // prefix는 @/common/utils/businessWizard.js 의 KEY_PREFIX와 동기화 ('business:wizard:done:').
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k && k.startsWith('business:wizard:done:')) {
        localStorage.removeItem(k);
      }
    }
  } catch (e) { /* private mode 등 */ }
};

export default store;
